<script setup>

import {router} from "@inertiajs/vue3";
import {computed, ref, watch} from "vue";
import UiTimeslots from "../../../widget/components/ui-components/UiTimeslots.vue";
import UiCalendar from "../../../widget/components/ui-components/UiCalendar.vue";
import InputText from "primevue/inputtext";
import ScrollPanel from "primevue/scrollpanel";
import Textarea from "primevue/textarea";
import InputMask from "primevue/inputmask";
import moment from "moment";
import _ from "lodash";
import {brightnessByColor} from "../../../../helpers.js";
import FormInput from "../../../widget/components/ui-components/FormInput.vue";

const selectedDay = ref(new Date())
const selectedTimeSlot = ref(1)
const currentTimeSlot = ref(null)

const props = defineProps({
    forms: Object,
    theme: Object,
    settings: Object,
    customForm: Array
})
const getFormattedDate = (date) => {
    return date.toISOString().split('T')[0];
}

const getEventDateTime = (timeString) => {
    const date = new Date();
    const [hours, minutes] = timeString.split(':').map(Number);
    date.setHours(hours, minutes, 0, 0);
    return date.toISOString();
}

const startDateToday = getFormattedDate(new Date());

const events = ref([
    {
        startDate: getEventDateTime('09:00'),
        endDate: getEventDateTime('10:00'),
        title: "Meeting",
        wholeDay: false
    },
    {
        startDate: getEventDateTime('11:00'),
        endDate: getEventDateTime('12:00'),
        title: "Workshop",
        wholeDay: false
    },
    {
        startDate: getEventDateTime('13:00'),
        endDate: getEventDateTime('14:00'),
        title: "Lunch Break",
        wholeDay: false
    },
    {
        startDate: getEventDateTime('17:00'),
        endDate: getEventDateTime('18:00'),
        title: "Review Meeting",
        wholeDay: false
    }
]);

const timeSlots = ref([
    {
        time_start: '07:00:00',
        time_end: '10:00:00',
        id: 1,
        disabled: true
    }, {
        time_start: '11:00:00',
        time_end: '15:00:00',
        id: 2,
        disabled: true
    }, {
        time_start: '16:00:00',
        time_end: '16:30:00',
        id: 3,
        disabled: true
    }, {
        time_start: '20:00:00',
        time_end: '20:10:00',
        id: 2,
        disabled: true
    }
]);

const dateTimeRestrictions = computed(() => {
    return {
        min: new Date(),
        max: moment().add(5, 'years').toDate(),
        disabled: [
            {
                repeat: {
                    weekdays: [1, 6],
                    until: new Date('2100-01-01')
                },
                start: new Date('2000-01-01')
            }
        ]
    }
});
const percentToHex = (percent) => {
    const decimal = Math.round((percent / 100) * 255);
    const hex = decimal.toString(16).toUpperCase();
    return hex.length === 1 ? '0' + hex : hex;
}

const currentBrightness = ref(brightnessByColor(props.theme.panel_color));

watch(() => props.theme.panel_color, _.debounce((val) => {
    currentBrightness.value = brightnessByColor(val)
}, 150), {immediate: true})
</script>

<template>
    <div
        class=" p-6 color-transition transition-all small-text"
        :class="[{
            'rounded-xl': theme.roundness === 1,
            'rounded-2xl': theme.roundness === 2,
            'rounded-3xl': theme.roundness === 3,
            'backdrop-filter backdrop-blur-lg': theme.panel_backdrop_blur,
            'border': theme.panel_transparency > 50,
            'border-neutral-700': theme.darkMode
        }]"
        :style="{
            backgroundColor: `#${theme.panel_color}${percentToHex(theme.panel_transparency)}`
        }"
    >
        <div class="absolute -top-4 -right-4">
            <slot name="panel"></slot>
        </div>
        <section id="basic-data" class="my-6 mb-9">
            <p class="text-xl font-bold text-center" :class="currentBrightness < 140 ? 'text-white': 'text-black'"
               v-html="forms.name"></p>
            <div class="p-4 pt-2" v-html="forms.description"></div>
        </section>
        <section id="date-time" class="flex gap-6 max-md:flex-col justify-center max-md:items-center ">
            <div class="relative">
                <div class="flex items-baseline mb-3 gap-2 text-lg pb-2"
                     :class="[theme.roundness < 3 ? 'border-b':'', theme.darkMode ? 'border-neutral-700' : '', currentBrightness < 140 ? 'text-white': 'text-black']">
                    <i class="pi pi-calendar text-gray-400" style="font-size: 1.25rem"></i>
                    {{ $t('forms.mock_date') }}
                </div>
                <UiCalendar
                    :key="`${settings.titlePosition}-${settings.showWeekNumbers}`"
                    v-model="selectedDay" :events="events"
                    :theme="theme"
                    :date-time-restrictions="dateTimeRestrictions"
                    :settings="settings"></UiCalendar>

                <div class="absolute -bottom-3 -right-16 z-50">
                    <slot name="calendar"></slot>
                </div>
            </div>
            <div class="grow relative  max-md:w-full">
                <div class="flex items-baseline mb-3 gap-2 text-lg pb-2"
                     :class="[theme.roundness < 3 ? 'border-b':'', theme.darkMode ? 'border-neutral-700' : '', currentBrightness < 140 ? 'text-white': 'text-black']">
                    <i class="pi pi-clock text-gray-400" style="font-size: 1.25rem"></i>
                    {{ $t('forms.mock_time') }}
                </div>
                <ScrollPanel style="height: 270px"
                             class="border bg-neutral-50 transition-all"
                             :class="{
                                    'rounded-xl': theme.roundness === 1,
                                    'rounded-2xl': theme.roundness === 2,
                                    'rounded-3xl': theme.roundness === 3,
                                    'bg-transparent border-neutral-700': theme.darkMode
                                }">
                    <UiTimeslots v-model="selectedTimeSlot" :events="[]" :theme="theme"
                                 :selected-date="selectedDay" :date-time-restrictions="dateTimeRestrictions"
                                 :time-slots="timeSlots"></UiTimeslots>
                </ScrollPanel>

                <div class="absolute -bottom-4 -right-4 z-50">
                    <slot name="roundness"></slot>
                </div>
            </div>
        </section>
        <section id="form">
            <div class="flex items-baseline mt-5 gap-2 text-lg pb-2"
                 :class="[theme.roundness < 3 ? 'border-b':'', theme.darkMode ? 'border-neutral-700' : '', currentBrightness < 140 ? 'text-white': 'text-black']">
                <i class="pi pi-info text-gray-400" style="font-size: 1.25rem"></i>
                {{ $t('forms.mock_form') }}
            </div>
            <div class="flex justify-center gap-6 mt-4 dark">
                <div class="grid grid-cols-2 gap-y-2 gap-x-4 grow">
                    <form-input :label="$t('forms.mock_name')" required type="text" :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"></form-input>
                    <form-input :label="$t('forms.mock_note')" class="row-span-2" rows="4" type="textarea" :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"></form-input>
                    <form-input label="Email" required type="text" :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"></form-input>
                    <form-input :label="$t('forms.mock_phone')" type="mask" mask="999 999 999" :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"></form-input>
                    <form-input v-for="input in customForm" :key="input" :type="input.type" :options="input.options"
                                :label="input.label" :required="input.required"
                                :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"></form-input>
                </div>
            </div>
        </section>
        <section id="ct-action" class="mt-8 flex justify-center">
            <div
                :class="`bg-${theme.color}-500 px-6 py-3 rounded-lg hover:bg-${theme.color}-600 transition-all cursor-pointer text-white`">
                {{ $t('forms.mock_book') }}
            </div>
        </section>
    </div>
</template>

<style scoped lang="scss">
.small-text {
    font-size: 12px!important;
}
.color-transition > * {
    transition: color .2s ease;
}
</style>
